export type ForecastRow = {
  date_axis: string;
  offer_country_name: string | null;
  offer_region: string | null;
  vendor_name: string | null;
  brand_bk: string | null;
  deal_tier: string | null;
  tier: string | null;
  actual_margin: number | null;
  expected_margin: number | null;
  blended_margin: number | null;
  actual_past: number | null;
  start_date: string | null;
  end_date: string | null;
  source_records: number;
};
export type ScoreRow = {
  week_of_date_axis: string;
  offer_country_name: string | null;
  top_week_historic_margin: number | null;
  expected_margin: number | null;
  actual_margin: number | null;
  margin_week_rank: number | null;
};
export const add = (date: string, n: number) =>
  new Date(Date.parse(date + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10);
export const monday = (date: string) => {
  const d = new Date(date + 'T00:00:00Z');
  return add(date, -((d.getUTCDay() + 6) % 7));
};
export const ratio = (a: number | null, b: number | null) => (a !== null && b !== null && b !== 0 ? a / b : null);
export function sum<T>(rows: T[], field: (r: T) => number | null): number | null {
  const v = rows.map(field).filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
  return v.length ? v.reduce((a, b) => a + b, 0) : null;
}
export function grouped<T>(rows: T[], key: (r: T) => string) {
  const result = new Map<string, T[]>();
  rows.forEach((r) => {
    const k = key(r),
      v = result.get(k);
    if (v) v.push(r);
    else result.set(k, [r]);
  });
  return [...result].map(([key, rows]) => ({ key, rows }));
}
export function scoreGroups(rows: ScoreRow[]) {
  return grouped(
    rows.filter((r) => r.offer_country_name !== null),
    (r) => r.offer_country_name!,
  )
    .map(({ key, rows }) => ({
      country: key,
      expected: sum(rows, (r) => r.expected_margin),
      actual: sum(rows, (r) => r.actual_margin),
      baseline: sum(rows, (r) => r.top_week_historic_margin),
      rank: sum(rows, (r) => r.margin_week_rank),
      score: ratio(
        sum(rows, (r) => r.expected_margin),
        sum(rows, (r) => r.top_week_historic_margin),
      ),
    }))
    .sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity));
}
export type HistoryRow = Pick<
  ForecastRow,
  'date_axis' | 'actual_margin' | 'expected_margin' | 'blended_margin' | 'actual_past'
> & { history_kind: 'pipeline' | 'comparison' | 'near' };
export function series(
  rows: Array<Pick<ForecastRow, 'date_axis' | 'actual_margin' | 'expected_margin' | 'blended_margin' | 'actual_past'>>,
  weekly: boolean,
  today: string,
) {
  return grouped(rows, (r) => (weekly ? monday(r.date_axis) : r.date_axis))
    .sort((a, b) => a.key.localeCompare(b.key))
    .map(({ key, rows }) => ({
      date: key,
      actual: sum(rows, (r) => r.actual_past),
      expected: sum(rows, (r) => r.expected_margin),
      blended: sum(rows, (r) => r.blended_margin),
      phase: key < (weekly ? monday(today) : today) ? 'Actual' : 'Forecast',
    }));
}
export function offerGroups(rows: ForecastRow[], includeUnknownCountry = false) {
  return grouped(
    rows.filter((r) => includeUnknownCountry || r.offer_country_name !== null),
    (r) => [r.offer_country_name, r.tier ?? 'Unknown', r.vendor_name ?? 'Unknown'].join('\u001f'),
  ).map(({ key, rows }) => ({
    key,
    country: rows[0].offer_country_name ?? 'Unknown',
    tier: rows[0].tier ?? 'Unknown',
    vendor: rows[0].vendor_name ?? 'Unknown',
    actual: sum(rows, (r) => r.actual_margin),
    expected: sum(rows, (r) => r.expected_margin),
    performance: ratio(
      sum(rows, (r) => r.actual_margin),
      sum(rows, (r) => r.expected_margin),
    ),
    rows,
  }));
}

/** Raw deal tiers the Flash schedule keeps; the Tours vendor is excluded. Matches the schedule dataset SQL. */
export const SCHEDULE_TIERS = [
  'Luxury Escapes – Tier 1',
  'Luxury Escapes – Tier 2',
  'Luxury Escapes – Tier 3',
  'Tier 1',
  'Tier 2',
  'Tier 3',
];
export const SCHEDULE_EXCLUDED_VENDOR = 'Tours By Luxury Escapes International';

export type PipelineScope = {
  today: string;
  dayMin: number;
  dayMax: number;
  countries: string[];
  regions: string[];
  tiers: string[];
  vendors: string[];
};
/** Region-level forecast records in the Flash schedule scope. Used for the full pipeline CSV. */
export function pipelineRows(rows: ForecastRow[], scope: PipelineScope): ForecastRow[] {
  const from = add(scope.today, scope.dayMin),
    to = add(scope.today, scope.dayMax);
  return rows.filter(
    (r) =>
      (!scope.countries.length || scope.countries.includes(r.offer_country_name ?? 'Unknown')) &&
      (!scope.regions.length || scope.regions.includes(r.offer_region ?? 'Unknown')) &&
      (!scope.tiers.length || scope.tiers.includes(r.tier ?? 'Unknown')) &&
      (!scope.vendors.length || scope.vendors.includes(r.vendor_name ?? 'Unknown')) &&
      r.date_axis >= from &&
      r.date_axis <= to &&
      r.vendor_name !== SCHEDULE_EXCLUDED_VENDOR &&
      SCHEDULE_TIERS.includes(r.tier || ''),
  );
}

/** One schedule record: an offer group and its daily actual (before today) and predicted margin. */
export type ScheduleRow = {
  offer_country_name: string | null;
  tier: string;
  vendor_name: string;
  cells: Array<[string, number | null]>;
};
export function scheduleGroups(rows: ScheduleRow[]) {
  return rows.map((r) => ({
    key: [r.offer_country_name, r.tier, r.vendor_name].join('\u001f'),
    country: r.offer_country_name ?? 'Unknown',
    tier: r.tier,
    vendor: r.vendor_name,
    days: new Map(r.cells),
  }));
}
/** Shading range for schedule cells: record-level low and high, widened to include 0 and 1. */
export function scheduleExtent(low: unknown, high: unknown): [number, number] {
  const finite = (value: unknown, fallback: number) => {
    const n = value === null || value === undefined || value === '' ? NaN : Number(value);
    return Number.isFinite(n) ? n : fallback;
  };
  return [Math.min(0, finite(low, 0)), Math.max(1, finite(high, 1))];
}

export function nextMonth(date: string) {
  const input = new Date(date + 'T00:00:00Z');
  const first = new Date(Date.UTC(input.getUTCFullYear(), input.getUTCMonth() + 1, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  first.setUTCDate(Math.min(input.getUTCDate(), last));
  return first.toISOString().slice(0, 10);
}
